import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApplyLoan } from "../../hooks/useLoans";
import { useMembers } from "../../hooks/useMembers";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { useAuth } from "../../context/AuthContext";
import { Target } from "lucide-react";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", currencyDisplay: "symbol", maximumFractionDigits: 0 }).format(n);

const STEPS = ["Loan details", "Choose sureties", "Review & submit"];

export default function ApplyLoanPage() {
  const { user }                       = useAuth();  
  const navigate     = useNavigate();

  if (user?.kyc_status != "verified") {alert('You are not verified for loan application yet!'); navigate("/loans");}
  
  const applyLoan    = useApplyLoan();
  const { data: members = [] } = useMembers() as any;

  const [step,    setStep]    = useState(0);
  const [amount,  setAmount]  = useState(1000000);
  const [purpose, setPurpose] = useState("");
  const [s1, setS1]           = useState("");
  const [s2, setS2]           = useState("");
  const [errors,  setErrors]  = useState<Record<string,string>>({});
  const [done,    setDone]    = useState(false);
  const [apiErr,  setApiErr]  = useState("");

  const loanReq  = amount * 0.5;
  //const eligible = (members as any[]).filter((m: any) => Number(m.balance) >= loanReq);
  // If using a library like Axios or a SQL client that returns an object  
  const eligible = (Array.isArray(members) ? members : []).filter(
    (m: any) => Number(m.balance) >= loanReq
  );

  const validate = () => {
    const errs: Record<string,string> = {};
    if (!s1) errs.s1 = "Required";
    if (!s2) errs.s2 = "Required";
    if (s1 && s2 && s1 === s2) errs.s2 = "Must be a different member";
    const m1 = (members as any[]).find((m: any) => m.member_id === s1);
    const m2 = (members as any[]).find((m: any) => m.member_id === s2);
    if (m1 && Number(m1.balance) < loanReq) errs.s1 = `Balance ${fmt(m1.balance)} < required ${fmt(loanReq)}`;
    if (m2 && Number(m2.balance) < loanReq) errs.s2 = `Balance ${fmt(m2.balance)} < required ${fmt(loanReq)}`;
    return errs;
  };

  const submit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    try {
      await applyLoan.mutateAsync({ amount, purpose, suretyIds: [s1, s2] });
      setDone(true);
    } catch (e: any) {
      setApiErr(Array.isArray(e?.errors) ? e.errors.join(" · ") : e?.error ?? "Submission failed");
    }
  };

  if (done) return (
    <div className="max-w-md mx-auto text-center pt-10">
      <div className="text-5xl mb-4">🎉</div>
      <h2 className="text-xl font-medium text-slate-800 mb-2">Application submitted!</h2>
      <p className="text-sm text-slate-500 mb-6">Your sureties have been notified and must sign off before funds are disbursed.</p>
      <Card className="mb-5 text-left">
        <p className="text-sm font-medium text-emerald-800 mb-3">Workflow status</p>
        {["✅ Application submitted","⏳ Awaiting surety 1 sign-off","⏳ Awaiting surety 2 sign-off","⏳ Fund disbursement"].map((s, i) => (
          <div key={i} className="flex gap-3 py-2 items-center">
            <span className="text-base">{s.slice(0,2)}</span>
            <p className="text-sm text-emerald-700">{s.slice(3)}</p>
          </div>
        ))}
      </Card>
      <Button onClick={() => navigate("/loans")}>View loans →</Button>
    </div>
  );

  return (
    <div className="max-w-lg mx-auto">
      {/* Step indicator */}
      <div className="flex gap-1.5 mb-6">
        {STEPS.map((s, i) => (
          <div key={i} className="flex-1 text-center">
            <div className="h-1 rounded-full mb-1 transition-all" style={{ background: i <= step ? "#059669" : "#e2e8f0" }}/>
            <span className="text-[10px]" style={{ color: i <= step ? "#059669" : "#94a3b8" }}>{s}</span>
          </div>
        ))}
      </div>

      {apiErr && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{apiErr}</div>}

      {/* Step 0: Loan details */}
      {step === 0 && (
        <Card>
          <h2 className="text-base font-medium text-slate-800 mb-5">Loan details & calculator</h2>
          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Loan amount: <span className="text-emerald-600 font-semibold">{fmt(amount)}</span>
            </label>
            <input type="range" min={5000} max={10000000} step={5000} value={amount}
              onChange={e => setAmount(Number(e.target.value))} className="w-full"/>
            <div className="flex justify-between text-xs text-slate-400 mt-1"><span>NGN5,000</span><span>NGN10,000,000</span></div>            
            <Input type="number" value={amount.toString()} onChange={(value) => setAmount(Number(value))} />
          </div>

          {/* Surety eligibility calculator */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5">
            <p className="text-sm font-medium text-emerald-800 mb-3">📊 Surety eligibility calculator</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div><p className="text-xs text-emerald-700 mb-0.5">Loan amount</p><p className="text-base font-medium text-emerald-800">{fmt(amount)}</p></div>
              <div><p className="text-xs text-emerald-700 mb-0.5">Each surety needs ≥</p><p className="text-base font-medium text-emerald-800">{fmt(loanReq)}</p></div>
            </div>
            <div className="border-t border-emerald-200 pt-3">
              <p className="text-xs text-emerald-700 mb-2">Eligible members ({eligible.length})</p>
              {eligible.length === 0
                ? <p className="text-xs text-red-700">No eligible sureties — reduce the loan amount</p>
                : eligible.slice(0, 4).map((m: any) => (
                    <div key={m.member_id} className="flex justify-between text-xs text-emerald-700">
                      <span>• {m.first_name} {m.last_name}</span>
                      <span> ✅</span>
                    </div>
                  ))
              }
            </div>
          </div>

          <Input label="Loan purpose" value={purpose} onChange={setPurpose}
            placeholder="e.g. Business expansion, Medical bills…"/>
          <Button onClick={() => setStep(1)} className="w-full justify-center" disabled={!purpose || eligible.length < 2}>
            Next: Choose sureties →
          </Button>
        </Card>
      )}

      {/* Step 1: Sureties */}
      {step === 1 && (
        <Card>
          <h2 className="text-base font-medium text-slate-800 mb-2">Select 2 sureties</h2>
          <div className="bg-blue-50 border border-blue-300 rounded-lg p-3 mb-5">
            <p className="text-xs text-blue-800">
              <strong>2-Surety Rule:</strong> Each surety must have ≥ {fmt(loanReq)} saved (50% of {fmt(amount)}) and be an active, KYC-verified member.
            </p>
          </div>
          {[
            { label:"Surety 1", val:s1, set:setS1, err:errors.s1 },
            { label:"Surety 2", val:s2, set:setS2, err:errors.s2 },
          ].map(({ label, val, set, err }) => (
            <div key={label} className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
              <select value={val} onChange={e => { set(e.target.value); setErrors({}); }}
                className={`w-full px-3 py-2.5 border rounded-lg text-sm bg-white outline-none ${err ? "border-red-400" : "border-slate-300"}`}>
                <option value="">— Select a member —</option>
                {(eligible as any[]).map((m: any) => (
                  <option key={m.member_id} value={m.member_id} disabled={Number(m.balance) < loanReq}>
                    {m.first_name} {m.last_name} — {Number(m.balance) >= loanReq ? "✅" : "❌"}
                  </option>
                ))}
              </select>
              {err && <p className="text-xs text-red-600 mt-1">⚠ {err}</p>}
            </div>
          ))}
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setStep(0)} className="flex-1 justify-center">← Back</Button>
            <Button onClick={() => { const errs = validate(); Object.keys(errs).length ? setErrors(errs) : setStep(2); }} className="flex-[2] justify-center">
              Review application →
            </Button>
          </div>
        </Card>
      )}

      {/* Step 2: Review */}
      {step === 2 && (
        <Card>
          <h2 className="text-base font-medium text-slate-800 mb-4">Review & confirm</h2>
          {[
            { label:"Loan amount",        value: fmt(amount) },
            { label:"Purpose",            value: purpose },
            { label:"Surety 1",           value: (members as any[]).find((m: any) => m.member_id === s1)?.first_name + " " + (members as any[]).find((m: any) => m.member_id === s1)?.last_name },
            { label:"Surety 2",           value: (members as any[]).find((m: any) => m.member_id === s2)?.first_name + " " + (members as any[]).find((m: any) => m.member_id === s2)?.last_name },
            { label:"Required per surety",value: fmt(loanReq) },
            { label:"Disbursement",       value: "After both sureties sign off" },
          ].map(row => (
            <div key={row.label} className="flex justify-between py-2.5 border-b border-slate-100 last:border-0">
              <span className="text-sm text-slate-500">{row.label}</span>
              <span className="text-sm font-medium text-slate-800">{row.value}</span>
            </div>
          ))}
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mt-4 mb-5">
            <p className="text-xs text-amber-800">
              By submitting, your sureties will receive a notification. Funds are only disbursed after both approve.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setStep(1)} className="flex-1 justify-center">← Back</Button>
            <Button onClick={submit} className="flex-[2] justify-center" disabled={applyLoan.isPending}>
              {applyLoan.isPending ? "Submitting…" : "✍️ Submit application"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
